import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import InteractiveAudioPlayer from "@/components/InteractiveAudioPlayer";
import { 
  PhoneIncoming, 
  PhoneOutgoing, 
  TrendingUp, 
  Headphones,
  CheckCircle2
} from "lucide-react";
import logo from "@/assets/holionix-logo.jpeg";
import inboundHero from "@/assets/inbound-support-hero-v2.jpg";
import outboundSalesHero from "@/assets/outbound-sales-hero-v2.jpg";
import marketingHero from "@/assets/marketing-campaigns-hero-v2.jpg";
import supportDeskHero from "@/assets/support-desk-hero-v2.jpg";

const Solutions = () => {
  const useCases = {
    inbound: {
      icon: PhoneIncoming,
      title: "Inbound Customer Support",
      subtitle: "24/7 Automated Support Excellence",
      image: inboundHero,
      challenges: [
        "High call volumes overwhelming human agents",
        "Long customer wait times (5-15 minutes average)",
        "24/7 coverage requires multiple expensive shifts",
        "Inconsistent service quality across agents"
      ],
      solutions: [
        "Unlimited concurrent call handling with zero wait time",
        "Instant natural language understanding and responses",
        "Perfect consistency across all interactions",
        "Intelligent escalation to human agents when needed"
      ],
      impact: [
        { metric: "40%", label: "Operational Cost Reduction" },
        { metric: "100%", label: "Availability (24/7/365)" },
        { metric: "90%", label: "First-Call Resolution" },
        { metric: "35%", label: "CSAT Improvement" }
      ]
    },
    outboundSales: {
      icon: PhoneOutgoing,
      title: "Outbound Sales",
      subtitle: "Scale Your Sales Team Infinitely",
      image: outboundSalesHero,
      challenges: [
        "Limited reach with human cold calling teams",
        "Expensive cost per lead ($50-100+)",
        "Inconsistent pitch quality and follow-ups",
        "Manual CRM updates slow down sales cycle"
      ],
      solutions: [
        "Scale to thousands of concurrent personalized calls",
        "Intelligent lead qualification and scoring",
        "Automated appointment scheduling",
        "Real-time CRM updates and detailed interaction logs"
      ],
      impact: [
        { metric: "3x", label: "More Qualified Leads" },
        { metric: "60%", label: "Lower Cost Per Lead" },
        { metric: "5x", label: "Outreach Capacity" },
        { metric: "50%", label: "Shorter Sales Cycle" }
      ]
    },
    marketing: {
      icon: TrendingUp,
      title: "Marketing Campaigns",
      subtitle: "Personalized Engagement at Scale",
      image: marketingHero,
      challenges: [
        "Low engagement rates with traditional methods",
        "High cost of telemarketing teams",
        "Difficulty tracking campaign ROI",
        "Slow manual campaign execution"
      ],
      solutions: [
        "Automated personalized outreach at massive scale",
        "Real-time engagement tracking and analytics",
        "Dynamic script adjustment based on responses",
        "Multi-touch follow-up automation"
      ],
      impact: [
        { metric: "4x", label: "Higher Engagement" },
        { metric: "70%", label: "Cost Reduction" },
        { metric: "100%", label: "Real-Time Tracking" },
        { metric: "2x", label: "Campaign Velocity" }
      ]
    },
    supportDesk: {
      icon: Headphones,
      title: "Voice Support Desk",
      subtitle: "Eliminate Ticket Backlogs",
      image: supportDeskHero,
      challenges: [
        "Ticket overload and long response times",
        "Repetitive inquiries burning out agents",
        "Inconsistent resolutions across channels",
        "Poor visibility into support metrics"
      ],
      solutions: [
        "Instant support via voice, SMS, WhatsApp, webchat",
        "Automatic ticket creation and intelligent routing",
        "Self-service resolution for common issues",
        "Seamless human handoff with full context"
      ],
      impact: [
        { metric: "80%", label: "Faster Response Time" },
        { metric: "60%", label: "Self-Service Resolution" },
        { metric: "45%", label: "Support Cost Reduction" },
        { metric: "90%", label: "Agent Satisfaction" }
      ]
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-shadow-glow">
              AI Voice Solutions for Every{" "}
              <span className="gradient-brand-text">Business Challenge</span>
            </h1>
            <p className="text-xl text-foreground/80 max-w-4xl mx-auto">
              From inbound support to outbound sales, Holionix transforms how enterprises communicate with customers.
            </p>
          </motion.div>

          <Tabs defaultValue="inbound" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-12 h-auto glass-effect">
              {Object.entries(useCases).map(([key, useCase]) => (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className="flex items-center gap-2 py-4 data-[state=active]:gradient-border"
                >
                  <useCase.icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{useCase.title.split(' ')[0]}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(useCases).map(([key, useCase]) => (
              <TabsContent key={key} value={key} className="space-y-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative rounded-2xl overflow-hidden mb-12"
                >
                  <img 
                    src={useCase.image} 
                    alt={useCase.title}
                    className="w-full h-64 md:h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent flex items-end p-8">
                    <div className="text-center w-full">
                      <div className="w-20 h-20 rounded-2xl gradient-brand mx-auto mb-6 flex items-center justify-center glow-effect">
                        <useCase.icon className="w-10 h-10 text-white" />
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold mb-4">{useCase.title}</h2>
                      <p className="text-xl text-accent font-semibold">{useCase.subtitle}</p>
                    </div>
                  </div>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-12">
                  <Card className="p-8 card-dark border-destructive/30">
                    <h3 className="text-2xl font-bold mb-6 text-destructive">Traditional Challenges</h3>
                    <ul className="space-y-4">
                      {useCase.challenges.map((challenge, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-foreground/70">
                          <span className="text-destructive">•</span> {challenge}
                        </li>
                      ))}
                    </ul>
                  </Card>

                  <Card className="p-8 card-dark gradient-border glow-effect">
                    <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                      <CheckCircle2 className="w-6 h-6 text-accent" /> Holionix Solution
                    </h3>
                    <ul className="space-y-4">
                      {useCase.solutions.map((solution, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                          <span className="font-medium">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </div>

                <Card className="p-10 card-dark">
                  <h3 className="text-3xl font-bold mb-8 text-center">Business Impact</h3>
                  <div className="grid md:grid-cols-4 gap-8">
                    {useCase.impact.map((item, idx) => (
                      <motion.div 
                        key={idx} 
                        className="text-center"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <div className="text-5xl font-bold gradient-brand-text mb-2 text-shadow-glow">{item.metric}</div>
                        <div className="text-sm text-foreground/60">{item.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </Card>

                <div className="max-w-xl mx-auto">
                  <h3 className="text-2xl font-bold mb-6 text-center">Hear It In Action</h3>
                  <div className="card-dark p-6 rounded-xl">
                    <InteractiveAudioPlayer
                      agentName={useCase.title}
                      agentRole={useCase.subtitle}
                      avatar={logo}
                      accent="American English"
                      audioType={key === 'inbound' ? 'inbound-support' : key === 'outboundSales' ? 'outbound-sales' : key === 'marketing' ? 'marketing' : 'support-desk'}
                    />
                  </div>
                </div>

                <div className="text-center">
                  <Link to="/contact">
                    <Button size="lg" className="gradient-brand text-white hover:opacity-90 text-lg px-10 py-7 glow-effect">
                      Schedule Demo
                    </Button>
                  </Link>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Solutions;
